// Accès aux données du chronomètre et des drills (D-098). Comme api.ts : on demande, la base ne renvoie
// que ce que le compte a le droit de voir (le staff du club lit les drills et les images ; une joueuse,
// le seul drill d'un bloc de sa prépa ; personne d'autre, rien).
import { supabase } from './supabase';
import type { DrillKind } from '../domain/drills';
import type { Category } from '../domain/session';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function rows<T>(q: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as T[];
}
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}
const chunks = <T>(xs: T[], n = 150) => Array.from({ length: Math.ceil(xs.length / n) }, (_, i) => xs.slice(i * n, i * n + n));
async function inIds<T>(ids: string[], q: (part: string[]) => PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<T[]> {
  const uniq = [...new Set(ids.filter(Boolean))];
  if (!uniq.length) return [];
  return (await Promise.all(chunks(uniq).map(part => rows<T>(q(part))))).flat();
}

export interface Drill {
  id: string; club_id: string; title: string; kind: DrillKind; config: Record<string, unknown>; is_model: boolean;
  proposed_at: string | null; proposed_by: string | null; created_by: string | null; created_at: string; updated_at: string;
  /** Supprimé de la bibliothèque (D-178) : seulement quand on le lit par son id pour une séance. */
  archived_at?: string | null;
}
const COLS = 'id, club_id, title, kind, config, is_model, proposed_at, proposed_by, created_by, created_at, updated_at';

/** Les drills du club (staff). */
export const clubDrills = (clubId: string) =>
  rows<Drill>(db().from('drills').select(COLS).eq('club_id', clubId).is('archived_at', null).order('updated_at', { ascending: false }));
/** Des drills précis : ceux qu'on a le droit de lire (une joueuse : celui de sa prépa). `withArchived` (D-178) : ceux d'une
 *  séance, même supprimés de la bibliothèque (la séance les garde tels quels). */
export const drillsByIds = (ids: string[], withArchived = false) => inIds<Drill>(ids, part => {
  const q = db().from('drills').select(`${COLS}, archived_at`).in('id', part);
  return withArchived ? q : q.is('archived_at', null);
});
export const drillById = async (id: string, withArchived = false) => (await drillsByIds([id], withArchived))[0] ?? null;

export async function saveDrill(d: { id?: string; club_id: string; title: string; kind: DrillKind; config: unknown; is_model?: boolean }): Promise<string> {
  const row = { title: d.title, kind: d.kind, config: d.config, is_model: d.is_model ?? false };
  const saved = d.id
    ? await must<{ id: string }[]>(db().from('drills').update(row).eq('id', d.id).select('id'))
    : [await must<{ id: string }>(db().from('drills').insert({ ...row, club_id: d.club_id }).select('id').single())];
  if (!saved[0]) throw new Error('réservé à son auteur');
  return saved[0].id;
}
/** Supprimer un drill (D-178), ou le remettre (« Annuler », `on` faux) : l'auteur ou le bureau ; refusé sinon. */
export async function archiveDrill(id: string, on = true): Promise<void> {
  const r = await must<{ id: string }[]>(db().from('drills').update({ archived_at: on ? new Date().toISOString() : null }).eq('id', id).select('id'));
  if (!r?.length) throw new Error('réservé à son auteur ou au bureau');
}
/** « Proposer au club » (responsable technique). */
export const proposeDrill = (id: string, on = true) => must(db().rpc('drill_propose', { p_drill: id, p_on: on }));

// --- Ma bibliothèque ---------------------------------------------------------------------------------
export const myDrillLibrary = async () => new Set((await rows<{ drill_id: string }>(db().from('drill_library').select('drill_id'))).map(r => r.drill_id));
export const addToDrillLibrary = (id: string) => must(db().from('drill_library').insert({ drill_id: id }));
export const removeFromDrillLibrary = (id: string) => must(db().from('drill_library').delete().eq('drill_id', id));

// --- Images du club (schémas, signes, logos de plays) --------------------------------------------------
export interface DrillImage { id: string; label: string; data: string; created_by: string | null }
export const clubDrillImages = (clubId: string) =>
  rows<DrillImage>(db().from('drill_images').select('id, label, data, created_by').eq('club_id', clubId).is('archived_at', null).order('created_at'));
export const addDrillImage = async (clubId: string, label: string, data: string) =>
  (await must<{ id: string }>(db().from('drill_images').insert({ club_id: clubId, label, data }).select('id').single())).id;
export const archiveDrillImage = (id: string) => must(db().from('drill_images').update({ archived_at: new Date().toISOString() }).eq('id', id));

// --- Lancements (staff) --------------------------------------------------------------------------------
export interface DrillRun { drill_id: string; team_id: string | null; launched_by: string; launched_at: string }
export const noteDrillRun = (drillId: string, teamId: string | null) => must(db().from('drill_runs').insert({ drill_id: drillId, team_id: teamId }));
/** Les derniers lancements des équipes (« Récemment »). */
export const recentRuns = (teamIds: string[], limit = 40) => (teamIds.length
  ? rows<DrillRun>(db().from('drill_runs').select('drill_id, team_id, launched_by, launched_at').in('team_id', teamIds).order('launched_at', { ascending: false }).limit(limit))
  : Promise.resolve([] as DrillRun[]));
/** Les lancements du club depuis une date (suivi du responsable technique). */
export const clubRuns = (clubId: string, since: string) =>
  rows<DrillRun>(db().from('drill_runs').select('drill_id, team_id, launched_by, launched_at').eq('club_id', clubId).gte('launched_at', since).limit(5000));

// --- Le chrono de l'équipe -------------------------------------------------------------------------------
export const teamChrono = async (teamId: string) => ((await rows<{ chrono: unknown }>(db().from('teams').select('chrono').eq('id', teamId)))[0]?.chrono ?? {});
export const setTeamChrono = (teamId: string, chrono: unknown) => must(db().rpc('team_set_chrono', { p_team: teamId, p_chrono: chrono }));

// --- L'exercice et son drill -----------------------------------------------------------------------------
export interface ExerciseLink { id: string; club_id: string; title: string; minutes: number | null; category: Category; description: string | null; drill_id: string | null; created_by: string | null }
const EX_COLS = 'id, club_id, title, minutes, category, description, drill_id, created_by';
export const exerciseLinks = (ids: string[]) => inIds<ExerciseLink>(ids, part => db().from('exercises').select(EX_COLS).in('id', part));
/** Les exercices du club qui ont un drill (« lié à l'exercice … »). */
export const clubExerciseLinks = (clubId: string) =>
  rows<ExerciseLink>(db().from('exercises').select(EX_COLS).eq('club_id', clubId).not('drill_id', 'is', null).is('archived_at', null));
/** Associe (ou retire) le drill d'un exercice ; faux si ce compte ne peut pas modifier l'exercice. */
export async function setExerciseDrill(exerciseId: string, drillId: string | null): Promise<boolean> {
  const r = await must<{ id: string }[]>(db().from('exercises').update({ drill_id: drillId }).eq('id', exerciseId).select('id'));
  return r.length > 0;
}

// --- Le thème du mois (responsable technique) --------------------------------------------------------------
export const themeDrillIds = async (themeId: string) =>
  (await rows<{ drill_id: string | null; position: number }>(db().from('theme_items').select('drill_id, position').eq('theme_id', themeId)));
export const addThemeItem = (row: { theme_id: string; position: number; title: string; minutes: number | null; category: Category; description: string | null; exercise_id: string | null; drill_id: string }) =>
  must(db().from('theme_items').insert(row));
